import { api } from '@/shared/api/client';
import type { Envelope } from '@/shared/api/types';
import type { PlaceCategory } from '@/shared/api/voting';

export interface PlaceResult {
  id: string;
  name: string;
  category: string;
  address: string;
  roadAddress: string;
  phone: string;
  x: number;
  y: number;
  placeUrl: string;
  /** 검색 중심점으로부터의 거리(m). 근처 추천 검색에서만 채워진다. */
  distanceM?: number | null;
  /** 평점(0~5). 서버에 Google 키가 없으면 null. */
  rating?: number | null;
  /** 리뷰 수. 평점과 함께 채워진다. */
  userRatingCount?: number | null;
}

/**
 * 정렬 기준. distance=거리순, accuracy=정확도순, rating=평점순.
 * search는 distance/accuracy만, nearby는 셋 다 지원한다.
 */
export type PlaceSort = 'distance' | 'accuracy' | 'rating';

interface PlaceSearchRes {
  places: PlaceResult[];
}

export async function searchPlaces(
  query: string,
  x?: string,
  y?: string,
  sort?: PlaceSort,
): Promise<PlaceResult[]> {
  const params = new URLSearchParams({ query });
  if (x) params.set('x', x);
  if (y) params.set('y', y);
  if (sort) params.set('sort', sort);
  const { data } = await api.get<Envelope<PlaceSearchRes>>(
    `/places/search?${params.toString()}`,
  );
  return data.data.places;
}

export interface NearbyParams {
  /** Kakao 카테고리 그룹 코드 (FD6 음식점 등). keyword만으로 검색할 땐 생략. */
  category?: string;
  /** 세부 카테고리 키워드 (일식, 노래방 등). 그룹 전체 검색이면 생략. */
  keyword?: string;
  x: number;
  y: number;
  radius?: number;
  /** 정렬 기준. 생략 시 서버 기본값(거리순)을 따른다. */
  sort?: PlaceSort;
}

/** 좌표 중심 카테고리/키워드 근처 장소 추천 (기본 거리순). */
export async function searchNearby(params: NearbyParams): Promise<PlaceResult[]> {
  const search = new URLSearchParams({
    x: String(params.x),
    y: String(params.y),
  });
  if (params.category) search.set('category', params.category);
  if (params.keyword) search.set('keyword', params.keyword);
  if (params.radius) search.set('radius', String(params.radius));
  if (params.sort) search.set('sort', params.sort);
  const { data } = await api.get<Envelope<PlaceSearchRes>>(
    `/places/nearby?${search.toString()}`,
  );
  return data.data.places;
}

interface PreferredRegionsRes {
  regions: string[];
}

/** 계정에 저장된 '주로 가는 지역' 목록 조회. 인증 필요 — 실패 시 throw(호출부가 캐시로 폴백). */
export async function getPreferredRegions(): Promise<string[]> {
  const { data } = await api.get<Envelope<PreferredRegionsRes>>(
    '/users/me/preferred-regions',
  );
  return data.data.regions;
}

/** '주로 가는 지역' 목록 저장. 인증 필요 — 실패 시 throw(호출부가 폴백 처리). */
export async function savePreferredRegions(
  regions: readonly string[],
): Promise<string[]> {
  const { data } = await api.put<Envelope<PreferredRegionsRes>>(
    '/users/me/preferred-regions',
    { regions },
  );
  return data.data.regions;
}

/** 미터 단위 거리를 "350m" / "1.2km" 형태로 표기. */
export function formatDistance(distanceM: number | null | undefined): string | null {
  if (distanceM == null || distanceM < 0) return null;
  if (distanceM < 1000) return `${distanceM}m`;
  return `${(distanceM / 1000).toFixed(1)}km`;
}

/** 지도 단축 링크(naver.me, kko.kakao.com 등)를 장소 정보로 변환. */
export async function resolvePlaceLink(url: string): Promise<PlaceResult> {
  const { data } = await api.post<Envelope<PlaceResult>>('/places/resolve-link', { url });
  return data.data;
}

/** 카카오 카테고리 문자열과 장소명 → 모이자 장소 카테고리. */
export function guessCategory(kakaoCategory: string, placeName = ''): PlaceCategory {
  const text = `${placeName} ${kakaoCategory}`.toLowerCase();
  if (/(보드게임|방탈출|노래방|볼링|pc방|피시방|만화카페|영화|공연|전시|문화|스포츠)/i.test(text)) {
    return 'ACTIVITY';
  }
  if (text.includes('음식점') || text.includes('식당') || text.includes('술집')) return 'MEAL';
  if (text.includes('카페') || text.includes('디저트') || text.includes('베이커리')) return 'CAFE';
  return 'ETC';
}
